import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type VisibilityState,
  type RowSelectionState,
  type ColumnFiltersState,
  type PaginationState,
  type FilterFn,
  type Row,
  getExpandedRowModel,
} from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu';
import {
  TrashIcon,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ColumnsIcon,
  ChevronDown,
  PlusIcon,
  CheckCircle,
  XCircle,
  TagIcon,
  FolderIcon,
  Download,
} from 'lucide-react';
import { Product, productService, ProductAttribute } from '@/services/productService';
import { useCategories } from '@/components/categories/SubcategoryManager/useCategories';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { stripHtml } from '@/utils/textUtils';
import { AttributeService } from '@/services/AttributeService';

import { useToast } from '@/components/ui/use-toast';
import { logError, logDebug } from '@/utils/errorHandler';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { DndContext, useSensor, useSensors, PointerSensor, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { ProductsSearchBox } from './ProductsSearchBox';
import { BulkTagModal } from './BulkTagModal';
import { BulkCategoryModal } from './BulkCategoryModal';

import { ProductsTableFallback } from '@/components/products/productstable/ProductsTableFallback';
import { IconBtn } from '@/components/products/productstable/IconBtn';
import { SortableTableHeader } from '@/components/products/productstable/SortableTableHeader';
import { useProductColumns } from '@/hooks/useProductColumns';
import ProductRowDetails from './productstable/ProductRowDetails';

import { CategoryModal } from './CategoryModal';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import { useFamilies } from '@/api/familyApi';
import { useProductAttributeGroups, useOptimizedProducts } from '@/hooks/useOptimizedProducts';

import { ROUTES } from '@/config/routes';
import { ProductsTableFilters } from './ProductsTableFilters';
import * as XLSX from 'xlsx';
import {
  createAttributeExportFieldsFromAttributes,
  createAttributeValueMap,
  formatAttributeValue,
  calculateAttributeColumnWidth,
  checkExcelLimits,
  type AttributeExportField,
  type EnrichedProduct,
} from '@/utils/attributeExportUtils';
import { useProductstableTranslations } from '@/hooks/useProductstableTranslations';
import { useFetchAllTags } from '@/hooks/useFetchTags';
import { PermissionGuard } from '@/components/common/PermissionGuard';
import { ConfirmationModal, useConfirmationModal } from '@/components/ui/ConfirmationModal';
import { useTranslation } from 'react-i18next';
import {
  createStatusFilterUpdate,
  debugStatusFilter,
  isValidStatusValue,
} from '@/utils/statusFilterUtils';

// Define filter state type
interface FilterState {
  category: string;
  family: string;
  status: 'all' | 'active' | 'inactive';
  minPrice: string;
  maxPrice: string;
  tags: string[]; // Add tags array to FilterState
  brand?: string;
  barcode?: string;
  created_at?: string;
  updated_at?: string;
  // Add new filter state fields
  is_active?: boolean | string;
  created_at_from?: string;
  created_at_to?: string;
  updated_at_from?: string;
  updated_at_to?: string;
}

// Add type for category options
interface CategoryOption {
  label: string;
  value: number | string; // Allow string ID if backend uses it
}

// Add this type declaration for productAttributes to match the Product interface
type ProductWithAttributeArray = Omit<Product, 'attributes'> & {
  attributes?: ProductAttribute[];
};

// Flattens a nested category tree into a flat array
function flattenCategories(categories: any[]): any[] {
  if (!Array.isArray(categories)) return [];

  return categories.reduce<any[]>((acc, cat) => {
    if (cat) acc.push(cat);
    if (cat && Array.isArray(cat.children) && cat.children.length > 0) {
      acc.push(...flattenCategories(cat.children));
    }
    return acc;
  }, []);
}

// Helper function to safely convert any category to CategoryOption format
function toCategoryOption(category: any): CategoryOption {
  if (!category) return { label: 'Uncategorized', value: 0 };

  // Handle case where category is already in the right shape
  if (category.label && (category.value || category.value === 0)) {
    return {
      label: String(category.label),
      value:
        typeof category.value === 'string'
          ? parseInt(category.value, 10) || category.value
          : Number(category.value),
    };
  }

  // Handle case where category is a regular Category object
  if (typeof category === 'object') {
    return {
      label: category.name || '',
      value:
        typeof category.id === 'string'
          ? parseInt(category.id, 10) || category.id
          : Number(category.id || 0),
    };
  }

  // Fallback for primitive values
  return {
    label: String(category),
    value: typeof category === 'string' ? category : Number(category || 0),
  };
}

// Add interface for ProductsTable props
interface ProductsTableProps {
  hideTopControls?: boolean;
  hideTopSearch?: boolean;
  filters?: Record<string, unknown>;
  uniqueBrands?: string[]; // Add brands prop to receive API-sourced brands
}

// Component for expanded row content with on-demand attribute loading
const ExpandedRowContent = React.memo(
  ({
    product,
    zebra,
    locale,
    channel,
    isExpanded,
    loadingText,
  }: {
    product: Product;
    zebra: boolean;
    locale: string;
    channel: string;
    isExpanded: boolean;
    loadingText: string;
  }) => {
    // Always call hooks first, then do conditional logic
    const shouldFetch =
      isExpanded && !!product?.id && !!locale && !!channel && locale !== '' && channel !== '';

    const { data: attributeGroups, isLoading: isLoadingAttributes } = useProductAttributeGroups(
      product.id || 0,
      shouldFetch, // More strict enabled condition
      { locale, channel }
    );

    // Early return after hooks are called
    if (!isExpanded || !product?.id) {
      return null;
    }

    if (isLoadingAttributes) {
      return (
        <tr className={cn('border-b', zebra ? 'bg-slate-950/3' : 'bg-white')}>
          <TableCell colSpan={999} className="p-4 text-center">
            <div className="text-sm text-muted-foreground">{loadingText}</div>
          </TableCell>
        </tr>
      );
    }

    const productWithAttributes: ProductWithAttributeArray = {
      ...product,
      attributes: attributeGroups || [],
    };

    return (
      <ProductRowDetails
        product={productWithAttributes as any}
        zebra={zebra}
        locale={locale}
        channel={channel}
      />
    );
  }
);

// Update the function signature to accept props
export function ProductsTable({
  hideTopControls = false,
  hideTopSearch = false,
  filters: initialFilters,
  uniqueBrands: propsBrands,
}: ProductsTableProps = {}) {
  // 🔍 DEBUG: Track ProductsTable renders
  const renderCountRef = useRef(0);
  renderCountRef.current += 1;

  // 🔍 DEBUG: Track what might be causing re-renders with detailed comparison
  const prevPropsRef = useRef<Record<string, unknown>>({});
  const currentProps = {
    hideTopControls,
    hideTopSearch,
    initialFilters: JSON.stringify(initialFilters || {}),
  };

  Object.keys(currentProps).forEach(key => {
    if (prevPropsRef.current[key] !== (currentProps as Record<string, unknown>)[key]) {
      logDebug('ProductsTable prop changed', {
        component: 'ProductsTable',
        action: 'propChange',
        propName: key,
        oldValue: prevPropsRef.current[key],
        newValue: (currentProps as Record<string, unknown>)[key],
      });
    }
  });
  prevPropsRef.current = currentProps;

  // Initialize translations hook
  const i18n = useProductstableTranslations();

  // 🔧 STABILITY FIX: Extract specific translations into stable variables to prevent infinite re-renders
  const stableTranslations = useMemo(
    () => ({
      uncategorized: i18n.getCategoryText('uncategorized'),
      failedToFetchData: i18n.getErrorText('failedToFetchData'),
      couldNotLoadProducts: i18n.getErrorText('couldNotLoadProducts'),
      pleaseRefreshPage: i18n.getErrorText('pleaseRefreshPage'),
    }),
    [i18n]
  );

  // Config is only used for technical configuration, not user-facing text
  const { toast } = useToast();
  const queryClient = useQueryClient();
  // Add organization settings hook
  const { defaultLocale, defaultChannel } = useOrgSettings({
    includePricing: false,
  });
  // Remove categoryOptions state - now using React Query computed value
  const [editingCell, setEditingCell] = useState<{
    rowIndex: number;
    columnId: string;
  } | null>(null);
  const [editValue, setEditValue] = useState<string | string[]>('');
  const [originalEditValue, setOriginalEditValue] = useState<string | string[]>('');

  // Use React Query to fetch families
  const { data: families = [], isLoading: isFamiliesLoading } = useFamilies();

  // Get the full category tree for breadcrumb building
  const { categories: categoryTree } = useCategories();

  // Add viewMode state
  const [viewMode] = useState<'list' | 'grid'>('list');

  // Categories are now fetched via React Query, no manual ref tracking needed

  // Add reference to track if user preferences have been loaded
  const prefsLoadedRef = useRef(false);

  const [searchTerm, setSearchTerm] = useState('');

  const [productRowMap, setProductRowMap] = useState<Record<string, number>>({});
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  // Add expanded rows state
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Table state
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  // Use prop pagination if provided, otherwise use internal state
  const [paginationState, setPaginationState] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50, // Changed from 10 to 50 to match PIM defaults
  });

  // Create computed pagination property that uses props if available
  const pagination = paginationState;
  // Create a function to update pagination that uses the prop function if available
  const setPagination = useCallback(
    (newPagination: PaginationState | ((prev: PaginationState) => PaginationState)) => {
      if (typeof newPagination === 'function') {
        setPaginationState(newPagination(paginationState));
      } else {
        setPaginationState(newPagination);
      }
    },
    [paginationState, setPaginationState]
  );

  // Use prop totalCount if provided, otherwise use internal state
  const [totalCountState, setTotalCountState] = useState<number>(0); // Changed from 100 to 0 to avoid misleading initial count
  // Create a computed totalCount property that uses props if available
  const totalCount = totalCountState;

  // Add scroll container ref
  const scrollRef = useRef<HTMLDivElement>(null);

  // Create a default filters object to ensure all keys are always present
  const defaultFilters = useMemo(
    () => ({
      page_size: pagination.pageSize, // Use current pagination.pageSize (50)
      page: pagination.pageIndex + 1,
      tags: [],
      category: 'all',
      family: undefined,
      status: 'all',
      minPrice: '',
      maxPrice: '',
      brand: '',
      barcode: '',
      created_at: '',
      updated_at: '',
      is_archived: false, // Exclude archived products by default
      ...initialFilters, // this will override defaults if provided
    }),
    [pagination.pageSize, pagination.pageIndex, initialFilters]
  );

  const [filters, setFilters] = useState<Record<string, any>>(() => defaultFilters);

  // Remove cleanFilters and pass filters directly to useFetchProducts
  const { data, isLoading, isFetching } = useOptimizedProducts(filters);

  // Track data and state changes - Fix missing 'filters' dependency
  useEffect(() => {
    logDebug('ProductsTable data changed', {
      component: 'ProductsTable',
      action: 'dataChange',
      dataLength: Array.isArray(data) ? data.length : data?.results?.length || 0,
      isLoading,
      isFetching,
      filtersCount: Object.keys(filters).length,
    });
  }, [data, isLoading, isFetching, filters]);

  // Use both isLoading and isFetching for complete loading state
  const loading = isLoading || isFetching;

  // Fetch organization total products count (excluding archived) using products stats endpoint
  const { data: productStats } = useQuery({
    queryKey: ['productStats'],
    queryFn: async () => {
      const response = await fetch('/api/products/stats/', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error(i18n.getErrorText('failedToFetchProductStats'));
      return response.json();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  // Get organization total products count (excluding archived)
  // Use product stats API which gives accurate organization total
  const organizationTotalProducts = productStats?.total_products || 0;

  // Calculate current filtered results count - make it consistent with products derivation
  const currentResultsCount = useMemo(() => {
    if (!data) return 0;

    // Handle the same way as products derivation
    if (Array.isArray(data)) {
      return data.length;
    } else if (Array.isArray(data?.results)) {
      return data.results.length;
    }

    return 0;
  }, [data]);

  // Process products for proper category display
  const filteredProducts = useMemo(() => {
    if (!data) return [];

    // For regular query, data is the direct response
    const currentPageProducts = Array.isArray(data)
      ? data
      : Array.isArray(data?.results)
        ? data.results
        : [];

    // Keep the original simple category structure - don't over-complicate it
    return currentPageProducts.map(p => ({
      ...p,
      // Use stable translation for uncategorized products
      category_name: p.category_name || stableTranslations.uncategorized,
    }));
  }, [data, stableTranslations.uncategorized]);

  // For compatibility with existing code, maintain products reference
  // const products = filteredProducts;

  // Set total count based on the response count – simplified & defensive
  useEffect(() => {
    // Helper – current page & size
    const currentPageIndex = (filters.page ?? pagination.pageIndex + 1) - 1; // zero-based
    const currentPageSize = filters.page_size ?? pagination.pageSize;

    if (data && typeof data === 'object' && 'count' in data && typeof data.count === 'number') {
      /* ------------------------------------------------------------------
       * Paginated DRF response => trust the server-side count field
       * ------------------------------------------------------------------ */
      setTotalCountState(data.count);
    } else if (Array.isArray(data)) {
      /* ------------------------------------------------------------------
       * Array response – we have NO reliable total. Use *conservative* calc:
       *  current page offset  +  number of items we actually received
       *  This guarantees we never pretend there is an extra page.
       * ------------------------------------------------------------------ */
      const conservativeTotal = currentPageIndex * currentPageSize + data.length;
      setTotalCountState(conservativeTotal);
    } else {
      // Unexpected / empty
      setTotalCountState(0);
    }
  }, [data, filters.page, filters.page_size, pagination.pageIndex, pagination.pageSize]);

  // Update filters when pagination or search changes
  useEffect(() => {
    const currentPage = pagination.pageIndex + 1;
    const currentPageSize = pagination.pageSize;

    // Only update if the values are actually different to prevent infinite loops
    if (filters.page !== currentPage || filters.page_size !== currentPageSize) {
      logDebug('Updating pagination filters', {
        component: 'ProductsTable',
        action: 'paginationUpdate',
        currentPage,
        currentPageSize,
        oldPage: filters.page,
        oldPageSize: filters.page_size,
      });
      setFilters(prev => ({
        ...prev,
        page: currentPage,
        page_size: currentPageSize,
      }));
    }
  }, [pagination.pageIndex, pagination.pageSize, filters.page, filters.page_size]);

  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  // State to control visibility of the Columns selector dropdown
  const [columnsMenuOpen, setColumnsMenuOpen] = useState<boolean>(false);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setColumnOrder(prevOrder => {
        const oldIndex = prevOrder.indexOf(active.id.toString());
        const newIndex = prevOrder.indexOf(over.id.toString());
        const newOrder = arrayMove(prevOrder, oldIndex, newIndex);
        sessionStorage.setItem('productTableColumnOrder', JSON.stringify(newOrder));
        return newOrder;
      });
    }
  }, []);

  // Use React Query to fetch categories - this prevents infinite loading issues
  const { data: categoriesData = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: productService.getCategories,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  // Process categories into options format using useMemo for stability
  const categoryOptions = useMemo(() => {
    if (!Array.isArray(categoriesData)) return [];

    // Flatten the entire tree so we include subcategories
    const flatCategories = flattenCategories(categoriesData);

    // Create category options from the flattened list
    return flatCategories.map(toCategoryOption);
  }, [categoriesData]);

  // Remove unused functions to clean up linter warnings
  // const filterCategories = (cats: CategoryType[], term: string): CategoryType[] => { ... }
  // const extractUniqueCategories = useCallback((products: Product[]) => { ... }
  // const uniqueCategories = useMemo(() => { ... }

  // Remove old useUniqueCategories hook usage
  // const uniqueCategories = useUniqueCategories(products);

  // Update the filteredData tag filtering logic with proper type casting
  const filteredData = filteredProducts;

  // Update the productRowMap whenever the filtered products change
  useEffect(() => {
    const newMap: Record<string, number> = {};
    // Only include products with valid ids
    filteredData.forEach((product, index) => {
      if (product && product.id !== undefined) {
        newMap[String(index)] = product.id;
      }
    });
    setProductRowMap(newMap);
  }, [filteredData]);

  // Categories are now loaded via React Query above, no manual useEffect needed

  // Update handleRefresh function
  const handleRefresh = useCallback(() => {
    // Invalidate all products queries to trigger a fresh fetch
    queryClient.invalidateQueries({
      queryKey: ['products'],
      exact: false, // This will invalidate all queries that start with ['products']
    });
    // Also refetch immediately
    queryClient.refetchQueries({
      queryKey: ['products'],
      exact: false,
    });
  }, [queryClient]);

  // Handle clear filters
  const handleClearFilters = useCallback(() => {
    // Preserve the current page size when resetting filters
    const currentPageSize = pagination.pageSize;
    setFilters({
      ...defaultFilters,
      page_size: currentPageSize, // Keep current page size
      page: 1, // Reset to first page
    });
    // Update URL params without the cleared filters
    const params = new URLSearchParams(searchParams);
    params.delete('category');
    params.delete('family');
    params.delete('status');
    params.delete('minPrice');
    params.delete('maxPrice');
    params.delete('tags');
    params.delete('brand');
    params.delete('barcode');
    params.delete('created_at');
    params.delete('updated_at');
    setSearchParams(params);
    // Also reset pagination to first page while preserving page size
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  }, [searchParams, setSearchParams, defaultFilters, pagination.pageSize, setPagination]);

  // --- ADD Bulk Action Handlers (Placeholder/Assumed API) ---
  const handleBulkDelete = async () => {
    const selectedCount = Object.keys(rowSelection).length;
    if (selectedCount === 0) return;

    // Confirm the archiving with the user (rename delete to archive)
    if (
      !window.confirm(
        i18n.getConfirmationText('bulkDelete').replace('{{count}}', selectedCount.toString())
      )
    ) {
      return;
    }

    // Get the product IDs of all selected rows
    const productIds = getSelectedProductIds();
    if (productIds.length === 0) return;

    logDebug('Bulk archive operation started', {
      component: 'ProductsTable',
      action: 'handleBulkDelete',
      productCount: productIds.length,
    });

    try {
      // Archive products (set is_archived = true) instead of deleting
      await productService.bulkArchive(productIds);

      logDebug('Bulk archive operation completed', {
        component: 'ProductsTable',
        action: 'handleBulkDelete',
        productCount: productIds.length,
      });

      // Show success toast
      toast({
        title: i18n.getSuccessText('bulkDelete').replace('{{count}}', productIds.length.toString()),
        variant: 'default',
      });

      // Clear row selection after successful archiving
      setRowSelection({});

      // Invalidate React Query cache to refresh the table immediately
      queryClient.invalidateQueries({ queryKey: ['products'], exact: false });
    } catch (error) {
      logError(
        'Failed to bulk archive products',
        {
          component: 'ProductsTable',
          action: 'handleBulkDelete',
          metadata: { productIds, error },
        },
        {
          showToast: true,
          userFacing: true,
        }
      );
    }
  };

  const handleBulkSetStatus = async (isActive: boolean) => {
    const selectedCount = Object.keys(rowSelection).length;
    if (selectedCount === 0) return;

    // Confirm the status change with the user
    const confirmMsg = isActive
      ? i18n.getConfirmationText('activate').replace('{{count}}', selectedCount.toString())
      : i18n.getConfirmationText('deactivate').replace('{{count}}', selectedCount.toString());

    if (!window.confirm(confirmMsg)) {
      return;
    }

    // Get the product IDs of all selected rows
    const productIds = getSelectedProductIds();
    if (productIds.length === 0) return;

    try {
      // 1. Optimistic update - update all selected products in cache immediately
      queryClient.setQueriesData({ queryKey: ['products'], exact: false }, (oldData: any) => {
        if (!oldData) return oldData;

        // Handle direct array response
        if (Array.isArray(oldData)) {
          return oldData.map((product: Product) =>
            product.id !== undefined && productIds.includes(product.id as number)
              ? { ...product, is_active: isActive }
              : product
          );
        }

        // Handle paginated response {results: [...], count: X}
        if (oldData.results && Array.isArray(oldData.results)) {
          return {
            ...oldData,
            results: oldData.results.map((product: Product) =>
              product.id !== undefined && productIds.includes(product.id as number)
                ? { ...product, is_active: isActive }
                : product
            ),
          };
        }

        return oldData;
      });

      // 2. API call in background
      await productService.bulkSetStatus(productIds, isActive);

      // Show success toast
      const successMsg = isActive
        ? i18n.getSuccessText('bulkActivate').replace('{{count}}', productIds.length.toString())
        : i18n.getSuccessText('bulkDeactivate').replace('{{count}}', productIds.length.toString());

      toast({
        title: successMsg,
        variant: 'default',
      });

      // Clear row selection after successful update
      setRowSelection({});

      // ✅ No cache invalidation needed - already updated optimistically
    } catch (error) {
      logError(
        `Failed to bulk ${isActive ? 'activate' : 'deactivate'} products`,
        {
          component: 'ProductsTable',
          action: 'handleBulkSetStatus',
          metadata: { productIds, isActive, error },
        },
        {
          showToast: true,
          userFacing: true,
        }
      );

      // ⚠️ Only invalidate on error to revert
      queryClient.invalidateQueries({ queryKey: ['products'], exact: false });
    }
  };

  // Get selected product IDs helper
  const getSelectedProductIds = useCallback(() => {
    // Since rowSelection keys are product IDs (from getRowId), convert them directly to numbers
    return Object.keys(rowSelection)
      .map(productIdString => parseInt(productIdString, 10))
      .filter((id): id is number => !isNaN(id));
  }, [rowSelection]);

  // Add loading state for export
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({ completed: 0, total: 0 });

  // Handle exporting selected products to XLSX with dynamic attribute columns
  const handleExportSelected = useCallback(async () => {
    const selectedIds = getSelectedProductIds();

    if (selectedIds.length === 0) {
      toast({
        title: i18n.getErrorText('noProductsSelected'),
        variant: 'destructive',
      });
      return;
    }

    setIsExporting(true);
    setExportProgress({ completed: 0, total: selectedIds.length });

    try {
      // Step 1: Discover all organization attributes for consistent column structure
      let organizationAttributes: AttributeExportField[] = [];
      try {
        // Fetch ALL organization attributes (not product-specific) for complete column structure
        const allOrgAttributes = await AttributeService.fetchAttributes();
        organizationAttributes = createAttributeExportFieldsFromAttributes(allOrgAttributes);

        logDebug('Discovered organization-wide attributes for export', {
          component: 'ProductsTable',
          attributeCount: organizationAttributes.length,
          totalOrgAttributes: allOrgAttributes.length,
        });
      } catch (error) {
        logError(
          'Failed to discover organization attributes',
          {
            component: 'ProductsTable',
            action: 'attributeDiscovery',
            metadata: { error },
          },
          { showToast: false, userFacing: false }
        );

        // Show warning but continue with basic export
        toast({
          title:
            i18n.getExcelWarningText?.('attributeDiscoveryFailed') ||
            'Could not load all attributes. Some attribute columns may be missing.',
          variant: 'default',
        });
      }

      // Step 2: Check Excel limits before proceeding
      const baseExportFields = [
        { field: 'sku', header: i18n.getExcelHeaderText('sku') },
        { field: 'name', header: i18n.getExcelHeaderText('name') },
        { field: 'description', header: i18n.getExcelHeaderText('description') },
        { field: 'category_name', header: i18n.getExcelHeaderText('category') },
        { field: 'family', header: i18n.getExcelHeaderText('family') },
        { field: 'brand', header: i18n.getExcelHeaderText('brand') },
        { field: 'tags', header: i18n.getExcelHeaderText('tags') },
        { field: 'barcode', header: i18n.getExcelHeaderText('gtin') },
        { field: 'prices', header: i18n.getExcelHeaderText('price') },
        { field: 'is_active', header: i18n.getExcelHeaderText('status') },
        { field: 'created_at', header: i18n.getExcelHeaderText('created') },
        { field: 'updated_at', header: i18n.getExcelHeaderText('lastModify') },
      ];

      const limitsCheck = checkExcelLimits(
        baseExportFields.length,
        organizationAttributes.length,
        selectedIds.length
      );

      // Show warnings if any
      if (limitsCheck.warnings.length > 0) {
        const warningMessage = limitsCheck.warnings.join(' ');
        toast({
          title: warningMessage,
          variant: 'default',
        });
      }

      // Block export if it would exceed Excel limits
      if (!limitsCheck.isValid) {
        const errorMessage = limitsCheck.errors.join(' ');
        toast({
          title: errorMessage,
          variant: 'destructive',
        });
        return;
      }

      // Step 3: Fetch enriched product data with attributes using optimized bulk method
      const enrichedProductsData = await productService.bulkGetProductsWithAttributes(selectedIds, {
        onProgress: (completed, total) => {
          setExportProgress({ completed, total });
        },
      });

      // Filter out failed fetches and transform to EnrichedProduct format
      const enrichedProducts: EnrichedProduct[] = enrichedProductsData.filter(Boolean).map(item => {
        const attributeValueMap = createAttributeValueMap(item!.effectiveAttributes);
        return {
          ...item!.product,
          attributeValues: attributeValueMap,
        };
      });

      if (enrichedProducts.length === 0) {
        toast({
          title: i18n.getErrorText('failedToExportProducts'),
          variant: 'destructive',
        });
        return;
      }

      // Step 4: Create combined export fields (base + attributes)
      const allExportFields = [...baseExportFields, ...organizationAttributes];

      // Step 5: Create header row
      const headers = allExportFields.map(f => f.header);

      // Step 6: Create data rows with formatted values
      const dataRows = enrichedProducts.map(product =>
        allExportFields.map(field => {
          // Handle attribute fields
          if ('attributeCode' in field) {
            const attrField = field as AttributeExportField;
            const attrData = product.attributeValues.get(attrField.attributeCode);

            if (!attrData || !attrData.hasRecord) {
              return ''; // No value set
            }

            return formatAttributeValue(attrData.value, attrField.attributeMetadata, i18n);
          }

          // Handle static fields (existing logic)
          const baseField = field as { field: string; header: string };
          const value = (product as any)[baseField.field];

          // Handle null/undefined values
          if (value === null || value === undefined) {
            return '';
          }

          // Special formatting for specific fields
          switch (baseField.field) {
            case 'category_name':
              if (Array.isArray(value)) {
                return value
                  .map(cat => (typeof cat === 'object' && cat?.name ? cat.name : String(cat)))
                  .join(', ');
              } else if (typeof value === 'object' && value?.name) {
                return value.name;
              } else {
                return String(value);
              }

            case 'family':
              if (typeof value === 'number') {
                const family = families.find(f => f.id === value);
                return family ? family.label : String(value);
              } else if (typeof value === 'object' && value?.label) {
                return value.label;
              } else {
                return String(value);
              }

            case 'prices':
              if (Array.isArray(value) && value.length > 0) {
                const listPrice = value.find(p => p.price_type === 'list') || value[0];
                return listPrice?.amount || '';
              } else {
                return '';
              }

            case 'tags':
              if (Array.isArray(value)) {
                return value
                  .map(tag => (typeof tag === 'object' && tag?.name ? tag.name : String(tag)))
                  .join(', ');
              } else {
                return String(value);
              }

            case 'is_active':
              return value
                ? i18n.getExcelStatusText('active')
                : i18n.getExcelStatusText('inactive');

            case 'description':
              if (typeof value === 'string') {
                return stripHtml(value);
              }
              return String(value);

            case 'created_at':
            case 'updated_at':
              try {
                const date = new Date(value);
                return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
              } catch {
                return String(value);
              }

            default:
              return String(value);
          }
        })
      );

      // Step 7: Create worksheet
      const worksheetData = [headers, ...dataRows];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

      // Step 8: Set column widths (base + dynamic attribute widths)
      const baseColumnWidths = [
        { wch: 15 }, // SKU
        { wch: 30 }, // Name
        { wch: 50 }, // Description
        { wch: 20 }, // Category
        { wch: 20 }, // Family
        { wch: 15 }, // Brand
        { wch: 25 }, // Tags
        { wch: 15 }, // GTIN
        { wch: 12 }, // Price
        { wch: 10 }, // Status
        { wch: 18 }, // Created
        { wch: 18 }, // Last Modify
      ];

      const attributeColumnWidths = organizationAttributes.map(attr =>
        calculateAttributeColumnWidth(attr.attributeMetadata)
      );

      const columnWidths = [...baseColumnWidths, ...attributeColumnWidths];
      worksheet['!cols'] = columnWidths;

      // Step 9: Create workbook and add worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, i18n.getExcelText('sheetName'));

      // Step 10: Generate filename with current date
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD format
      const filename = `products-export-${dateStr}.xlsx`;

      // Step 11: Write and download the file
      XLSX.writeFile(workbook, filename);

      // Step 12: Show success message
      const totalColumns = baseExportFields.length + organizationAttributes.length;
      toast({
        title:
          i18n.getSuccessText('exportedProducts', {
            count: enrichedProducts.length,
          }) + ` (${totalColumns} columns)`,
        variant: 'default',
      });
    } catch (error) {
      logError(
        'Failed to export products with attributes',
        {
          component: 'ProductsTable',
          action: 'handleExportSelected',
          metadata: { error },
        },
        {
          showToast: false,
          userFacing: false,
        }
      );
      toast({
        title: i18n.getErrorText('failedToExportProducts'),
        description:
          i18n.getExcelErrorText?.('attributeFetchFailed') ||
          'Failed to fetch product attributes for export',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
      setExportProgress({ completed: 0, total: 0 });
    }
  }, [getSelectedProductIds, families, i18n, toast]);

  // --- Derived Data ---
  // Helper function to safely update a product in the React Query cache
  const updateProductInCache = useCallback(
    (productId: number, updateFn: (product: Product) => Product) => {
      // Update ALL products queries (regardless of filter params) using predicate
      queryClient.setQueriesData({ queryKey: ['products'], exact: false }, (oldData: any) => {
        if (!oldData) return oldData;

        // Handle direct array response
        if (Array.isArray(oldData)) {
          return oldData.map((product: Product) =>
            product.id === productId ? updateFn(product) : product
          );
        }

        // Handle paginated response {results: [...], count: X}
        if (oldData.results && Array.isArray(oldData.results)) {
          return {
            ...oldData,
            results: oldData.results.map((product: Product) =>
              product.id === productId ? updateFn(product) : product
            ),
          };
        }

        return oldData;
      });
    },
    [queryClient]
  );

  // Update the updateData function to use optimistic updates
  const updateData = useCallback(
    async (rowIndex: number, columnId: string, value: any) => {
      logDebug('Product update operation started', {
        component: 'ProductsTable',
        action: 'updateData',
        rowIndex,
        columnId,
        valueType: typeof value,
      });

      const productId = productRowMap[String(rowIndex)];

      if (!productId) {
        logDebug('Product ID not found for row', {
          component: 'ProductsTable',
          action: 'updateData',
          rowIndex,
        });
        return;
      }

      try {
        // Format the value based on the column type
        let formattedValue = value;

        // Special handling for different column types
        if (columnId === 'price') {
          formattedValue = parseFloat(value);
          if (isNaN(formattedValue) || formattedValue < 0) {
            toast({
              title: i18n.getErrorText('invalidPriceValue'),
              variant: 'destructive',
            });
            return;
          }
        } else if (columnId === 'is_active') {
          formattedValue = value === 'true' || value === true;
        }

        // Create API payload with correct field names
        let apiPayload: Record<string, any>;

        if (columnId === 'category') {
          // This is now handled directly in the CategoryTreeSelect onChange
          return;
        } else {
          // For all other fields, use the column ID as the key
          apiPayload = {
            [columnId]: formattedValue,
          };
        }

        logDebug('Product update API call', {
          component: 'ProductsTable',
          action: 'updateData',
          productId,
          columnId,
          payload: apiPayload,
        });

        // 1. Optimistic update in cache (immediate UI change)
        updateProductInCache(productId, product => ({
          ...product,
          [columnId]: formattedValue,
        }));

        // 2. API call in background
        await productService.updateProduct(productId, apiPayload);

        logDebug('Product update completed successfully', {
          component: 'ProductsTable',
          action: 'updateData',
          productId,
          columnId,
        });

        // Show success notification
        toast({
          title: i18n.getSuccessText('fieldUpdated', {
            field: columnId.charAt(0).toUpperCase() + columnId.slice(1),
          }),
          variant: 'default',
        });
        // ✅ No cache invalidation needed - already updated optimistically

        setEditingCell(null); // Clear editing state
      } catch (error) {
        logError(
          'Failed to update product in cache',
          {
            component: 'ProductsTable',
            action: 'updateData',
            metadata: { productId, error },
          },
          {
            showToast: false,
            userFacing: false,
          }
        );
        toast({
          title: i18n.getErrorText('failedToUpdateProduct'),
          variant: 'destructive',
        });

        // ⚠️ Only invalidate on error to revert
        queryClient.invalidateQueries({ queryKey: ['products'], exact: false });
      }
    },
    [productRowMap, toast, queryClient, updateProductInCache, i18n]
  );

  // Move the price cell input handler to avoid recreating it on each render
  const handlePriceCellChange = useCallback((_e: React.ChangeEvent<HTMLInputElement>) => {
    const value = _e.target.value;
    // Only allow numbers and a single decimal point
    if (/^$|^[0-9]+(\.[0-9]*)?$/.test(value)) {
      setEditValue(value);
    }
  }, []);

  // Handle starting cell editing
  const handleCellEdit = useCallback(
    (rowIndex: number, columnId: string, value: string | string[]) => {
      setEditingCell({ rowIndex, columnId });

      // Handle special case for price - remove currency formatting
      if (columnId === 'price') {
        // Remove currency symbols and formatting, keep only numbers and decimal
        const numericValue = (value as string).replace(/[^0-9.]/g, '');
        setEditValue(numericValue);
        setOriginalEditValue(numericValue);
      } else {
        setEditValue(value);
        setOriginalEditValue(value);
      }
    },
    []
  );

  // Handle saving cell edit
  const handleSaveCellEdit = useCallback(() => {
    logDebug('Cell edit save operation', {
      component: 'ProductsTable',
      action: 'handleSaveCellEdit',
      hasEditingCell: !!editingCell,
      columnId: editingCell?.columnId,
      valueType: typeof editValue,
    });

    if (editingCell) {
      if (editingCell.columnId === 'tags') {
        let tagsArray: string[] = [];

        if (Array.isArray(editValue)) {
          // Handle case where editValue is already an array
          tagsArray = editValue
            .map(tag => {
              if (typeof tag === 'string') return tag;
              if (tag && typeof tag === 'object' && tag !== null) {
                const tagObj = tag as Record<string, unknown>;
                if (typeof tagObj.label === 'string') return tagObj.label;
                if (typeof tagObj.value === 'string') return tagObj.value;
              }
              return '';
            })
            .filter(Boolean);
        } else if (typeof editValue === 'string') {
          try {
            // First try to parse it as JSON (our new format)
            const parsed = JSON.parse(editValue);
            if (Array.isArray(parsed)) {
              tagsArray = parsed;
            } else {
              // Fall back to original comma-splitting logic
              tagsArray = editValue
                .split(',')
                .map(tag => tag.trim())
                .filter(Boolean);
            }
          } catch {
            // If JSON parsing fails, use the original comma-splitting logic
            tagsArray = editValue
              .split(',')
              .map(tag => tag.trim())
              .filter(Boolean);
          }
        }

        logDebug('Tags processed for update', {
          component: 'ProductsTable',
          action: 'handleSaveCellEdit',
          tagCount: tagsArray.length,
        });
        updateData(editingCell.rowIndex, editingCell.columnId, tagsArray);
      } else {
        logDebug('Non-tags cell edit processed', {
          component: 'ProductsTable',
          action: 'handleSaveCellEdit',
          columnId: editingCell.columnId,
        });
        updateData(editingCell.rowIndex, editingCell.columnId, editValue);
      }
    }
  }, [editingCell, editValue, updateData]);

  // Handle canceling cell edit
  const handleCancelEdit = useCallback(() => {
    logDebug('Cell edit canceled', {
      component: 'ProductsTable',
      action: 'handleCancelEdit',
    });
    setEditValue(originalEditValue); // Restore the original value
    setEditingCell(null);
  }, [originalEditValue]);

  // Handle key press in editable cell
  const handleKeyDown = useCallback(
    (_e: React.KeyboardEvent<HTMLInputElement>) => {
      if (_e.key === 'Enter') {
        handleSaveCellEdit();
      } else if (_e.key === 'Escape') {
        handleCancelEdit();
      }
    },
    [handleSaveCellEdit, handleCancelEdit]
  );

  // Handle row click for navigation to product detail - MOVED HERE BEFORE COLUMNS DEFINITION
  const handleRowClick = useCallback(
    (productId: number) => {
      // If currently editing a field, just cancel the edit without navigating
      if (editingCell) {
        handleCancelEdit();
        return; // Prevent navigation on this click
      }

      // Otherwise, navigate to product detail
      navigate(`/app/products/${productId}`);
    },
    [navigate, editingCell, handleCancelEdit]
  );

  // Remove allBrands state - brands will come from API via props

  // Use cached tags hook instead of manual loading
  const {
    data: cachedTagOptions = [],
    // Remove unused variables
  } = useFetchAllTags({
    staleTime: 30 * 60 * 1000, // 30 minutes - adjust as needed
    gcTime: 60 * 60 * 1000, // 1 hour cache
  });

  // Use cached tags as the tagOptions
  const tagOptions = cachedTagOptions;

  // Remove the local brand extraction - brands will come from API via props

  // Improve the function to handle tag creation for inline editing
  const handleCreateTagOption = useCallback(
    async (inputValue: string) => {
      if (!inputValue) return null;
      try {
        const newTag = await productService.createTag({ name: inputValue });
        const newOption = { label: newTag.name, value: newTag.name };

        // Invalidate and refetch tags cache instead of local state
        await queryClient.invalidateQueries({ queryKey: ['product-tags'] });

        toast({
          title: i18n.getSuccessText('tagCreated').replace('{{name}}', inputValue),
          variant: 'default',
        });

        return newOption;
      } catch {
        toast({
          title: i18n.getErrorText('tagCreation'),
          variant: 'destructive',
        });
        return null;
      }
    },
    [queryClient, toast, i18n]
  );

  // Function to render expanded row content with attributes
  const renderExpandedRow = (row: Row<Product>, index: number) => {
    // Safety check to ensure row.original exists
    if (!row?.original) return null;

    const productId = row.original.id;

    if (!productId) {
      return (
        <ProductRowDetails
          key={`expanded-${row.id}-product-undefined`}
          product={row.original as any}
          zebra={index % 2 === 0}
          locale={defaultLocale || ''}
          channel={defaultChannel?.code || ''}
        />
      );
    }

    return (
      <ExpandedRowContent
        key={`expanded-${row.id}-product-${productId}`}
        product={row.original}
        zebra={index % 2 === 0}
        locale={defaultLocale || ''}
        channel={defaultChannel?.code || ''}
        isExpanded={row.getIsExpanded()}
        loadingText={i18n.getStateText('loadingAttributes')}
      />
    );
  };

  // ROW EXPANSION
  // Create the expander column with proper memoization
  const expanderColumn = useMemo<ColumnDef<Product>>(
    () => ({
      id: 'expander',
      // Keep header minimal
      header: () => <div className="w-9"></div>,
      cell: ({ row }) => {
        const toggle = row.getToggleExpandedHandler();

        return (
          <button
            type="button"
            tabIndex={0}
            role="button"
            aria-label={
              row.getIsExpanded()
                ? i18n.getStateText('collapseRow')
                : i18n.getStateText('expandRow')
            }
            onClick={_e => {
              toggle();
            }}
            onKeyDown={_e => {
              if (_e.key === 'Enter' || _e.key === ' ') {
                _e.preventDefault();
                toggle();
              }
            }}
            /* 🔑 fill whole cell */
            className="absolute inset-0 flex cursor-pointer items-center justify-center
                       border-none bg-transparent
                       hover:bg-accent/60 focus:outline-none dark:hover:bg-accent/40"
          >
            <ChevronRight className={cn('h-4 w-4', row.getIsExpanded() && 'rotate-90')} />
          </button>
        );
      },
      enableSorting: false,
      enableHiding: false, // Make sure it can't be hidden
      size: 36,
      minSize: 36, // Ensure minimum width
      maxSize: 36, // Ensure maximum width
    }),
    [i18n] // Add dependencies for translation function
  );

  // 🔧 CRITICAL FIX: Much simpler memoization without creating new references
  // Use only length-based dependencies to avoid creating new strings/arrays
  const stableTagOptions = useMemo(() => tagOptions, [tagOptions]);
  const stableCategoryOptions = useMemo(() => categoryOptions, [categoryOptions]);
  const stableEditingCell = useMemo(() => editingCell, [editingCell]);
  const stableEditValue = useMemo(() => editValue, [editValue]);
  const stableCategoryTree = useMemo(() => categoryTree, [categoryTree]);

  // Create stable toast handler to prevent infinite re-renders
  const stableToast = useCallback(
    (msg: { title: string; description?: string; variant?: 'default' | 'destructive' }) => {
      toast(msg);
    },
    [toast]
  );

  // Add the handleDelete function
  const { t: tGlobal } = useTranslation('translation');
  const archiveModal = useConfirmationModal();
  const [productToArchive, setProductToArchive] = useState<Product | null>(null);

  const performArchive = useCallback(async () => {
    if (!productToArchive?.id) return;

    const id = productToArchive.id;

    // Optimistic removal
    queryClient.setQueriesData({ queryKey: ['products'], exact: false }, (oldData: any) => {
      if (!oldData) return oldData;
      if (Array.isArray(oldData)) {
        return oldData.filter((p: Product) => p.id !== id);
      }
      if (oldData.results && Array.isArray(oldData.results)) {
        return {
          ...oldData,
          results: oldData.results.filter((p: Product) => p.id !== id),
          count:
            typeof oldData.count === 'number' && oldData.count > 0
              ? oldData.count - 1
              : oldData.count,
        };
      }
      return oldData;
    });

    try {
      await productService.deleteProduct(id);
      queryClient.invalidateQueries({ queryKey: ['products'], exact: false });
      toast({ title: i18n.getSuccessText('productDeleted'), variant: 'default' });
    } catch (_error) {
      queryClient.invalidateQueries({ queryKey: ['products'], exact: false });
      logError(
        'Failed to archive product',
        { component: 'ProductsTable', action: 'performArchive', metadata: { id, _error } },
        { showToast: true, userFacing: true }
      );
    }
  }, [productToArchive, queryClient, toast, i18n]);

  const handleDelete = useCallback(
    (product: Product) => {
      setProductToArchive(product);
      archiveModal.openModal();
    },
    [archiveModal]
  );

  const { columns, allColumns } = useProductColumns({
    editingCell: stableEditingCell,
    editValue: stableEditValue,
    tagOptions: stableTagOptions,
    categoryOptions: stableCategoryOptions,
    categoryTree: stableCategoryTree,

    setEditValue,
    setCategoryOptions: () => {}, // No-op since categories are managed by React Query

    handleKeyDown,
    handlePriceCellChange,
    handleSaveCellEdit,
    handleCancelEdit,
    handleCellEdit,
    handleCreateTagOption,
    updateData,

    handleRowClick,
    handleDelete,
    toast: stableToast,
    IconBtn,
  });

  // Memoize allColumnsWithExpander to prevent re-creation on each render
  const allColumnsWithExpander = useMemo(() => {
    const selectColumn = allColumns.find(col => col.id === 'select');
    const restColumns = allColumns.filter(col => col.id !== 'select');

    if (selectColumn) {
      // If there's a select column, keep it first, then expander, then other columns
      return [selectColumn, expanderColumn, ...restColumns];
    } else {
      // If no select column, expander goes first
      return [expanderColumn, ...restColumns];
    }
  }, [expanderColumn, allColumns]);

  // Initialize column order from saved state or create default order
  useEffect(() => {
    /* run only once — prevents page-size from jumping back */
    if (prefsLoadedRef.current || columns.length === 0) return;
    prefsLoadedRef.current = true;

    const loadUserPreferences = () => {
      try {
        // Load column order
        const savedOrder = localStorage.getItem('productTableColumnOrder');
        if (savedOrder) {
          try {
            const parsedOrder = JSON.parse(savedOrder);
            // Validate that parsedOrder is an array and filter out any null/undefined values
            if (Array.isArray(parsedOrder) && parsedOrder.length > 0) {
              // Ensure expander column is first
              const orderWithExpander = ['expander'];
              parsedOrder.forEach(colId => {
                if (colId !== 'expander') {
                  orderWithExpander.push(colId);
                }
              });
              setColumnOrder(orderWithExpander);
            } else {
              // Fall back to default if saved order is invalid
              createDefaultOrder();
            }
          } catch (e) {
            logError(
              'Failed to parse column order from localStorage',
              {
                component: 'ProductsTable',
                action: 'useEffect',
                metadata: { error: e },
              },
              {
                showToast: false,
                userFacing: false,
              }
            );
            createDefaultOrder();
          }
        } else {
          createDefaultOrder();
        }

        // Load column visibility
        const savedVisibility = localStorage.getItem('productTableColumnVisibility');
        if (savedVisibility) {
          // Ensure expander column is always visible
          const visibilitySettings = JSON.parse(savedVisibility);
          setColumnVisibility({
            ...visibilitySettings,
            expander: true,
          });
        }

        // Load page size
        const savedPageSize = localStorage.getItem('productTablePageSize');
        if (savedPageSize) {
          const parsedPageSize = parseInt(savedPageSize, 10);
          // Validate that the saved page size is one of our valid options
          const validPageSizes = [25, 50, 100, 250];
          const validatedPageSize = validPageSizes.includes(parsedPageSize) ? parsedPageSize : 50;

          setPagination({
            ...pagination,
            pageSize: validatedPageSize,
          });
        }
      } catch (e) {
        logError(
          'Failed to load user preferences',
          {
            component: 'ProductsTable',
            action: 'loadUserPreferences',
            metadata: { error: e },
          },
          {
            showToast: false,
            userFacing: false,
          }
        );
        createDefaultOrder();
      }
    };

    const createDefaultOrder = () => {
      // Initialize with default order, making sure expander is first
      const defaultOrder = ['expander'];

      columns
        .map(column => {
          return (
            column.id ||
            // @ts-expect-error - handle accessorKey which may exist on some column types
            (column.accessorKey ? column.accessorKey.toString() : '')
          );
        })
        .filter(Boolean) // Filter out any null or empty strings
        .forEach(colId => {
          if (colId !== 'expander') {
            defaultOrder.push(colId);
          }
        });

      setColumnOrder(defaultOrder);
    };

    loadUserPreferences();
  }, [columns, pagination, setPagination]);

  // Update the handleExpandedChange function to load assets when a row is expanded
  type ExpandedState = Record<string, boolean>;
  const handleExpandedChange = useCallback((updater: (prev: ExpandedState) => ExpandedState) => {
    setExpanded(prev => {
      const next = updater(prev);

      // Note: Removed asset fetching logic since products are now read-only from React Query
      // Asset fetching should be handled at the API level or in a separate hook

      // Return the full next state to preserve all expanded rows
      return next;
    });
  }, []);

  // Determine pageCount for React Table – rely on DRF 'next' when available
  const derivedPageCount = useMemo(() => {
    // If backend sent paginated object with `next`
    if (data && typeof data === 'object' && 'next' in (data as any)) {
      const hasNext = Boolean((data as any).next);
      // If there is a `next` URL ⇒ at least one more page exists
      if (hasNext) return pagination.pageIndex + 2; // current page (0-based) + next page
      // No `next` ⇒ current page is last
      return pagination.pageIndex + 1;
    }
    // Fallback to math using totalCount
    return Math.max(1, Math.ceil(totalCount / pagination.pageSize));
  }, [data, pagination.pageIndex, pagination.pageSize, totalCount]);

  // Configure the table with useReactTable
  const table = useReactTable<Product>({
    data: filteredData,
    columns: allColumnsWithExpander,
    getRowId: row => String(row.id), // ✅ Stable row identity based on product ID
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination,
      expanded,
    },
    manualPagination: true,
    manualFiltering: true, // Disable client-side filtering - we use server-side filtering
    pageCount: derivedPageCount,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    meta: {
      updateData: (_rowIndex: number, _columnId: string, _value: unknown) => {
        // Implementation remains the same
      },
    },
    filterFns: {
      // All custom filter functions remain the same
    } as Record<string, FilterFn<Product>>,
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: updater => {
      const newState = typeof updater === 'function' ? updater(pagination) : updater;
      setPagination(newState);
    },
    onColumnOrderChange: setColumnOrder,
    onExpandedChange: handleExpandedChange as Parameters<
      typeof useReactTable
    >[0]['onExpandedChange'],
    getExpandedRowModel: getExpandedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    // Allow every row to be expandable even without subRows
    getRowCanExpand: () => true,
    // Remove getFilteredRowModel here
  });

  // Handle search input change
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  // Column filter state handlers - removed unused state variables

  // Save user preferences when they change
  useEffect(() => {
    // Save column order
    localStorage.setItem('productTableColumnOrder', JSON.stringify(columnOrder));
  }, [columnOrder]);

  useEffect(() => {
    // Save column visibility
    localStorage.setItem('productTableColumnVisibility', JSON.stringify(columnVisibility));
  }, [columnVisibility]);

  useEffect(() => {
    // Save page size
    localStorage.setItem('productTablePageSize', JSON.stringify(pagination.pageSize));
  }, [pagination.pageSize]);

  // Add these new state variables for modals
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);

  // Modal handlers
  const openBulkCategoryModal = useCallback(() => {
    setShowCategoryModal(true);
  }, []);

  const openBulkTagModal = useCallback(() => {
    setShowTagModal(true);
  }, []);

  // Add this function to extract unique tags from products for the filter dropdown
  // Use all available tags from API, not just from current products
  const uniqueTags = useMemo(() => {
    return tagOptions.map(tag => tag.label).sort();
  }, [tagOptions]);

  // Now update the handleFilterChange function to ensure tags are properly handled
  const handleFilterChange = useCallback(
    <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
      // Always reset to first page when any filter changes
      setPagination(prev => ({ ...prev, pageIndex: 0 }));

      if (key === 'family') {
        setFilters(prev => ({
          ...prev,
          family: value === 'all' ? undefined : value,
          page: 1,
        }));
        return;
      }

      // Handle status filter coming as 'status' from ProductsTableFilters
      if (key === 'status') {
        const statusValue = value as 'all' | 'active' | 'inactive' | 'true' | 'false';

        // Debug the status filter change
        debugStatusFilter('ProductsTable', filters, `handleFilterChange status: ${statusValue}`);

        // Validate the status value
        if (!isValidStatusValue(statusValue)) {
          logError(new Error(`Invalid status value received: ${statusValue}`), {
            component: 'ProductsTable',
            action: 'handleFilterChange',
            metadata: { statusValue, key },
          });
          return;
        }

        // Use centralized utility to create proper filter update
        const updatedFilters = createStatusFilterUpdate(statusValue, filters);
        setFilters(updatedFilters);
        return;
      }

      // Handle date filters directly (they come with the exact keys from ProductsTableFilters)
      if (
        key === 'created_at_from' ||
        key === 'created_at_to' ||
        key === 'updated_at_from' ||
        key === 'updated_at_to'
      ) {
        const dateValue = value as string;
        if (dateValue === '') {
          // Remove this specific date filter
          setFilters(prev => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { [key]: removed, ...rest } = prev;
            return { ...rest, page: 1 };
          });
        } else {
          // Set the date filter value
          setFilters(prev => ({ ...prev, [key]: dateValue, page: 1 }));
        }
        return;
      }

      // Special handling for price filters
      if (key === 'minPrice' || key === 'maxPrice') {
        const stringValue = value as string;
        // Convert empty strings to undefined, keep valid numbers as strings
        const processedValue = stringValue === '' ? undefined : stringValue;
        setFilters(prev => ({ ...prev, [key]: processedValue, page: 1 }));
        return;
      }

      // Special handling for barcode filter
      if (key === 'barcode') {
        const barcodeValue = value as string;
        const processedValue = barcodeValue === '' ? undefined : barcodeValue;
        setFilters(prev => ({ ...prev, barcode: processedValue, page: 1 }));
        return;
      }

      // Default handling for all other filters
      setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
    },
    [setPagination, filters]
  );

  // Add a function to calculate pagination display info
  const renderPaginationInfo = useCallback(() => {
    const { pageIndex, pageSize } = table.getState().pagination;
    // Use organization total for pagination display instead of filtered total
    const totalItems = organizationTotalProducts;
    const start = totalItems > 0 ? pageIndex * pageSize + 1 : 0;
    const end = Math.min(totalItems, (pageIndex + 1) * pageSize);
    const template =
      i18n.getPaginationText('showingRange') || 'Showing {{start}}-{{end}} of {{total}}';
    return template
      .replace('{{start}}', start.toString())
      .replace('{{end}}', end.toString())
      .replace('{{total}}', totalItems.toString());
  }, [table, organizationTotalProducts, i18n]);

  // Handle click outside to cancel editing
  useEffect(() => {
    if (!editingCell) return;

    const handleClickOutside = (event: MouseEvent) => {
      // Click outside detected - potential edit cancellation

      // Check if the click is outside all edit fields
      const target = event.target as HTMLElement;

      // Get the element that is currently being edited
      const editContainer = document.querySelector('[data-editing="true"]');

      // If the click is within the edit container, allow it
      if (editContainer && editContainer.contains(target)) {
        // Click is within edit container - allowing
        return;
      }

      // Special handling for React Select components which might be outside their containers
      if (
        // Target has any class that starts with "react-select"
        target.closest('[class*="react-select"]') ||
        // Common React Select classes
        target.closest('.css-26l3qy-menu, .css-1s2u09g-control, .css-1pahdxg-control') ||
        // Role-based selection for dropdown components
        target.closest('[role="listbox"], [role="option"], [role="combobox"]') ||
        // Check for data attribute we'll add to the AsyncCreatableSelect container
        target.closest('[data-component="async-select"]') ||
        // Check for category tree select popover
        target.closest('[data-testid="category-popover"]') ||
        // Check for any radix popover content
        target.closest('[data-radix-popper-content-wrapper]')
      ) {
        // Click is within react-select/dropdown component - allowing
        return;
      }

      // Handle clicks on buttons inside the editing area (confirm/cancel)
      if (
        target.closest('button') &&
        editContainer &&
        editContainer.contains(target.closest('button'))
      ) {
        return;
      }

      // If we got here, the click was outside the editing area
      handleCancelEdit();
    };

    // Better approach: Use focusout event for reliable blur detection
    const handleFocusOut = (event: FocusEvent) => {
      const editContainer = document.querySelector('[data-editing="true"]');
      if (!editContainer) return;

      // Check if focus is moving to another element within the edit container
      const relatedTarget = event.relatedTarget as HTMLElement;
      if (relatedTarget && editContainer.contains(relatedTarget)) {
        return;
      }

      // Check for React Select or other dropdown components - IMPROVED
      if (
        relatedTarget &&
        (relatedTarget.closest('[class*="react-select"]') ||
          relatedTarget.closest('[role="listbox"], [role="option"], [role="combobox"]') ||
          relatedTarget.closest('[data-component="async-select"]') ||
          relatedTarget.closest('[data-testid="category-popover"]') ||
          relatedTarget.closest('[data-radix-popper-content-wrapper]'))
      ) {
        return;
      }

      // ADDITIONAL CHECK: If relatedTarget is null (common with React Select),
      // check if any React Select menus are currently open
      if (!relatedTarget) {
        const hasOpenReactSelectMenu =
          document.querySelector('[class*="react-select__menu"]') ||
          document.querySelector('.react-select__menu') ||
          document.querySelector('[role="listbox"]');
        if (hasOpenReactSelectMenu) {
          return;
        }
      }

      // SPECIAL CHECK: If we're editing tags, be extra lenient with React Select
      const isEditingTags = editContainer.querySelector('[class*="react-select"]');
      if (isEditingTags) {
        // For tag editing with React Select, use a much longer delay
        setTimeout(() => {
          // Only cancel if React Select menu is completely closed AND
          // focus has moved to something completely unrelated
          const stillEditing = document.querySelector('[data-editing="true"]');
          const activeEl = document.activeElement;
          const hasOpenReactSelectMenu =
            document.querySelector('[class*="react-select__menu"]') ||
            document.querySelector('.react-select__menu') ||
            document.querySelector('[role="listbox"]');

          // Don't cancel if:
          // 1. React Select menu is still open
          // 2. Focus is on any React Select component
          // 3. Focus is on the body (common during React Select interactions)
          if (
            hasOpenReactSelectMenu ||
            (activeEl && activeEl.closest('[class*="react-select"]')) ||
            activeEl === document.body
          ) {
            return;
          }

          // Only cancel if focus has clearly moved to something unrelated to editing
          if (
            stillEditing &&
            activeEl &&
            !stillEditing.contains(activeEl) &&
            !activeEl.closest('[class*="react-select"]') &&
            !activeEl.closest('button')
          ) {
            // Don't cancel when focus moves to save/cancel buttons

            handleCancelEdit();
          }
        }, 500); // Much longer delay for tag editing
        return;
      }

      // Use a longer delay to handle React Select interactions properly
      setTimeout(() => {
        // Double-check that we're still editing and focus hasn't returned
        const stillEditing = document.querySelector('[data-editing="true"]');
        const activeEl = document.activeElement;

        // More thorough check for React Select elements
        const isInReactSelect =
          activeEl &&
          (activeEl.closest('[class*="react-select"]') ||
            activeEl.closest('[role="listbox"], [role="option"], [role="combobox"]') ||
            activeEl.closest('[data-component="async-select"]'));

        // Also check if any React Select menus are still open
        const hasOpenReactSelectMenu =
          document.querySelector('[class*="react-select__menu"]') ||
          document.querySelector('.react-select__menu') ||
          document.querySelector('[role="listbox"]');

        if (
          stillEditing &&
          activeEl &&
          !stillEditing.contains(activeEl) &&
          !isInReactSelect &&
          !hasOpenReactSelectMenu
        ) {
          handleCancelEdit();
        }
      }, 150); // Increased delay to handle React Select interactions
    };

    // Use both approaches for maximum reliability
    document.addEventListener('pointerdown', handleClickOutside);
    document.addEventListener('focusout', handleFocusOut);

    // Cleanup
    return () => {
      document.removeEventListener('pointerdown', handleClickOutside);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, [editingCell, handleCancelEdit]);

  // Use API-sourced brands from props, fallback to extracting from current products
  const uniqueBrands = useMemo(() => {
    // Use brands from props if available (API-sourced)
    if (propsBrands && propsBrands.length > 0) {
      return propsBrands;
    }

    // Fallback: extract from current products
    const brands = new Set<string>();
    filteredProducts.forEach(p => {
      if (p && typeof p.brand === 'string' && p.brand.trim()) brands.add(p.brand.trim());
    });
    return Array.from(brands).sort();
  }, [propsBrands, filteredProducts]);

  // After we have the `table` instance
  const canNextPage = useMemo(() => {
    // Prefer backend signal when available
    if (data && typeof data === 'object' && 'next' in (data as any)) {
      return Boolean((data as any).next);
    }
    return table.getCanNextPage();
  }, [data, table]);

  // Render the component
  return (
    <React.Fragment>
      <div className="flex min-h-0 flex-1 flex-col overflow-x-hidden px-2 lg:px-4">
        {/* Table Toolbar - Updated with better enterprise styling */}
        <div className="enterprise-card-elevated mb-4 flex items-center justify-between rounded-lg border border-border bg-card px-6 py-4">
          <div className="flex items-center space-x-6">
            {/* Product count info - integrated into toolbar */}
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-semibold text-foreground">
                  {currentResultsCount.toLocaleString()}
                </span>
                <span className="text-xs font-medium text-muted-foreground">
                  {i18n.getProductsCountText(currentResultsCount, organizationTotalProducts)}
                </span>
              </div>
            </div>

            {/* Search box */}
            {!hideTopSearch && (
              <div className="relative max-w-xs flex-1">
                <ProductsSearchBox
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="w-full"
                  data-cy="search-input"
                />
              </div>
            )}
          </div>

          <div className="flex items-center space-x-3">
            {/* Pagination info - compact with better styling */}
            <div className="mr-4 flex items-center space-x-1 text-xs text-muted-foreground">
              <span>{i18n.getPaginationText('page')}</span>
              <span className="rounded border border-border bg-muted px-2 py-1 font-mono text-xs text-foreground">
                {pagination.pageIndex + 1}
              </span>
              <span>{i18n.getPaginationText('of')}</span>
              <span className="rounded border border-border bg-muted px-2 py-1 font-mono text-xs text-foreground">
                {Math.ceil(
                  (organizationTotalProducts || currentResultsCount) / pagination.pageSize
                ) || 1}
              </span>
            </div>

            {/* Export Selected button */}
            <PermissionGuard permission="product.change">
              <Button
                size="sm"
                variant="outline"
                className="enterprise-button-secondary h-9"
                disabled={Object.keys(rowSelection).length === 0 || isExporting}
                onClick={handleExportSelected}
                isLoading={isExporting}
                loadingText={
                  exportProgress.total > 0
                    ? `Exporting... ${exportProgress.completed}/${exportProgress.total}`
                    : 'Preparing export...'
                }
              >
                <Download className="mr-1 size-3.5" />
                {i18n.getToolbarText('exportSelected')}
              </Button>
            </PermissionGuard>

            {/* Only show refresh button if hideTopControls is false */}
            {!hideTopControls && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleRefresh}
                disabled={loading}
                className="enterprise-button-secondary h-9"
              >
                <RefreshCw className={`mr-1 size-3.5 ${loading ? 'animate-spin' : ''}`} />
                {i18n.getToolbarText('refresh')}
              </Button>
            )}

            {/* Always show the Category Manager */}
            <PermissionGuard permission="product.change">
              <div className="flex items-center">
                <Button
                  size="sm"
                  variant="outline"
                  className="enterprise-button-secondary h-9"
                  onClick={() => setShowCategoryManager(true)}
                >
                  <FolderIcon className="mr-2 size-4" />
                  {i18n.getToolbarText('bulkCategory')}
                </Button>
              </div>
            </PermissionGuard>

            {/* Bulk Actions and other buttons */}
            <PermissionGuard permission="product.change">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="enterprise-button-secondary h-9"
                    disabled={Object.keys(rowSelection).length === 0}
                    data-cy="bulk-actions"
                  >
                    <span>
                      {i18n.getToolbarText('bulkActions')}
                      {Object.keys(rowSelection).length > 0
                        ? ` (${Object.keys(rowSelection).length})`
                        : ''}
                    </span>
                    <ChevronDown className="ml-2 size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => handleBulkSetStatus(true)}>
                    <CheckCircle className="mr-2 size-4 text-green-500" />
                    {i18n.getToolbarText('bulkActivate')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleBulkSetStatus(false)}>
                    <XCircle className="mr-2 size-4 text-red-500" />
                    {i18n.getToolbarText('bulkDeactivate')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={openBulkTagModal}>
                    <TagIcon className="mr-2 size-4" />
                    {i18n.getToolbarText('bulkTags')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={openBulkCategoryModal}>
                    <FolderIcon className="mr-2 size-4" />
                    {i18n.getToolbarText('bulkCategory')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <PermissionGuard permission="product.delete">
                    <DropdownMenuItem onClick={handleBulkDelete}>
                      <TrashIcon className="mr-2 size-4 text-red-500" />
                      {i18n.getToolbarText('bulkDelete')}
                    </DropdownMenuItem>
                  </PermissionGuard>
                </DropdownMenuContent>
              </DropdownMenu>
            </PermissionGuard>

            <DropdownMenu open={columnsMenuOpen} onOpenChange={setColumnsMenuOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="enterprise-button-secondary h-9"
                  aria-label={i18n.getColumnVisibilityText('toggle')}
                >
                  <ColumnsIcon className="mr-1 size-4" />
                  <span>{i18n.getColumnVisibilityText('title')}</span>
                  <ChevronDown className="ml-1 size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {table
                  .getAllColumns()
                  .filter(column => column.id !== 'expander' && column.id !== 'select')
                  .map(column => {
                    return (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        checked={column.getIsVisible()}
                        onSelect={e => e.preventDefault()}
                        onCheckedChange={value => column.toggleVisibility(!!value)}
                      >
                        {i18n.getColumnVisibilityText(`columnNames.${column.id}`) ||
                          column.id.charAt(0).toUpperCase() + column.id.slice(1).replace('_', ' ')}
                      </DropdownMenuCheckboxItem>
                    );
                  })}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Only show New Product button if hideTopControls is false and user has add permission */}
            {!hideTopControls && (
              <PermissionGuard permission="product.add">
                <Button className="enterprise-button h-9" asChild data-cy="new-product">
                  <Link to={ROUTES.APP.NEW}>
                    <PlusIcon className="mr-2 size-4" />
                    {i18n.getToolbarText('newProduct')}
                  </Link>
                </Button>
              </PermissionGuard>
            )}
          </div>
        </div>

        {/* Section containing scroll area and footer */}
        <section className="flex min-h-0 flex-1 flex-col">
          {viewMode === 'list' ? (
            <>
              <div
                ref={scrollRef}
                className="min-h-0 flex-1 overflow-auto"
                id="products-scroll-area"
              >
                <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
                  <SortableContext
                    items={columnOrder.filter(Boolean)}
                    strategy={horizontalListSortingStrategy}
                  >
                    <Table className="min-w-full table-fixed border-collapse">
                      <TableHeader className="sticky top-0 z-20 border-b border-border bg-card">
                        {table.getHeaderGroups().map(headerGroup => (
                          <React.Fragment key={headerGroup.id}>
                            {/* 1) Column titles */}
                            <TableRow className="sticky top-0 z-30 border-b border-border bg-muted/60 hover:bg-muted/80">
                              {headerGroup.headers.map(header => (
                                <SortableTableHeader
                                  key={header.id}
                                  id={header.column.id}
                                  header={header}
                                />
                              ))}
                            </TableRow>

                            {/* 2) Filter inputs - replaced with ProductsTableFilters */}
                            <ProductsTableFilters
                              columns={columns}
                              filters={filters}
                              onFilterChange={(columnId, value) =>
                                handleFilterChange(columnId as keyof FilterState, value)
                              }
                              onClearFilters={handleClearFilters}
                              table={table}
                              uniqueCategories={categoryOptions}
                              uniqueTags={uniqueTags}
                              uniqueBrands={uniqueBrands}
                              families={families}
                              isFamiliesLoading={isFamiliesLoading}
                            />
                          </React.Fragment>
                        ))}
                      </TableHeader>
                      <TableBody>
                        <ProductsTableFallback
                          columns={columns}
                          loading={loading}
                          filteredData={filteredData}
                          filters={filters}
                          handleClearFilters={handleClearFilters}
                          handleRefresh={handleRefresh}
                        />

                        {!loading &&
                          filteredData.length > 0 &&
                          table.getRowModel().rows.map((row, index: number) => {
                            // Add a safety check to ensure row.original exists
                            if (!row?.original) return null;

                            const productId = row.original.id;
                            // Skip this row if productId is undefined
                            if (productId === undefined) return null;

                            return (
                              <React.Fragment key={row.id || `row-${index}`}>
                                <TableRow
                                  data-state={row.getIsSelected() && 'selected'}
                                  className={cn(
                                    'border-b border-border hover:bg-accent/60',
                                    row.getIsSelected()
                                      ? 'bg-primary/10 border-primary/20'
                                      : index % 2 === 0
                                        ? 'bg-background'
                                        : 'bg-muted/30',
                                    'h-0 leading-tight'
                                  )}
                                  onClick={e => {
                                    const target = e.target as HTMLElement;

                                    // Enhanced action click detection to handle button children (like SVG icons)
                                    const isActionClick = !!target.closest(
                                      [
                                        'button',
                                        'input',
                                        '[role="combobox"]',
                                        '[data-editable="true"]',
                                        '[data-editing="true"]',
                                        '[data-component="category-tree-select-container"]',
                                        '[data-component="async-select"]',
                                        '[data-testid="category-popover"]',
                                        '.react-select__control',
                                        '.react-select__menu',
                                      ].join(', ')
                                    );

                                    // Don't do anything if clicking on an action element
                                    if (isActionClick) {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      return;
                                    }

                                    // Only navigate if not editing - don't auto-cancel on row clicks
                                    // Let users explicitly use the cancel button or click outside
                                    if (!editingCell && productId) {
                                      handleRowClick(productId);
                                    }
                                  }}
                                >
                                  {row.getVisibleCells().map((cell: any) => {
                                    const columnId = cell.column.id;
                                    const hideOnMobileClass = [
                                      'brand',
                                      'barcode',
                                      'created_at',
                                      'tags',
                                    ].includes(columnId)
                                      ? 'hidden md:table-cell'
                                      : '';

                                    // Special styling for expander column
                                    if (columnId === 'expander') {
                                      return (
                                        <TableCell
                                          key={cell.id}
                                          className="relative w-9 p-0"
                                          data-column-id={columnId}
                                        >
                                          {flexRender(
                                            cell.column.columnDef.cell,
                                            cell.getContext()
                                          )}
                                        </TableCell>
                                      );
                                    }

                                    const isActionsColumn = columnId === 'actions';
                                    const actionsClass = isActionsColumn
                                      ? 'sticky right-0 z-10 bg-card'
                                      : '';
                                    const cellBgClass = isActionsColumn
                                      ? row.getIsSelected()
                                        ? 'bg-primary/10'
                                        : index % 2 === 0
                                          ? 'bg-background'
                                          : 'bg-muted/30'
                                      : '';

                                    // Special cell rendering for specific column types
                                    if (columnId === 'brand' && row.getValue('brand')) {
                                      return (
                                        <TableCell
                                          key={cell.id}
                                          className={`px-2 py-0 ${hideOnMobileClass} ${actionsClass} ${cellBgClass}`}
                                          data-column-id={columnId}
                                        >
                                          {flexRender(
                                            cell.column.columnDef.cell,
                                            cell.getContext()
                                          )}
                                        </TableCell>
                                      );
                                    }

                                    // Add special treatment for family column
                                    if (columnId === 'family') {
                                      return (
                                        <TableCell
                                          key={cell.id}
                                          className={`min-w-[180px] px-2 py-1 ${hideOnMobileClass} ${actionsClass} ${cellBgClass}`}
                                          data-column-id={columnId}
                                        >
                                          {flexRender(
                                            cell.column.columnDef.cell,
                                            cell.getContext()
                                          )}
                                        </TableCell>
                                      );
                                    }

                                    if (columnId === 'category_name') {
                                      // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                      const categoryValue = row.getValue('category_name') as string;
                                      return (
                                        <TableCell
                                          key={cell.id}
                                          className={`px-2 py-1 ${hideOnMobileClass} ${actionsClass} ${cellBgClass}`}
                                          data-column-id={columnId}
                                        >
                                          <div className="flex items-center gap-1">
                                            <span className="text-muted-foreground">
                                              <FolderIcon className="size-3.5" />
                                            </span>
                                            {flexRender(
                                              cell.column.columnDef.cell,
                                              cell.getContext()
                                            )}
                                          </div>
                                        </TableCell>
                                      );
                                    }

                                    if (columnId === 'is_active') {
                                      const isActive = row.getValue('is_active') as boolean;
                                      return (
                                        <TableCell
                                          key={cell.id}
                                          className={`px-2 py-1 ${hideOnMobileClass} ${actionsClass} ${cellBgClass}`}
                                          data-column-id={columnId}
                                        >
                                          <div className="flex items-center gap-1">
                                            {isActive ? (
                                              <CheckCircle className="size-4 text-green-600" />
                                            ) : (
                                              <XCircle className="size-4 text-red-600" />
                                            )}
                                            {flexRender(
                                              cell.column.columnDef.cell,
                                              cell.getContext()
                                            )}
                                          </div>
                                        </TableCell>
                                      );
                                    }

                                    // Default cell rendering
                                    return (
                                      <TableCell
                                        key={cell.id}
                                        className={`px-2 py-1 ${hideOnMobileClass} ${actionsClass} ${cellBgClass}`}
                                        data-column-id={columnId}
                                        style={isActionsColumn ? { width: 112 } : undefined}
                                      >
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                      </TableCell>
                                    );
                                  })}
                                </TableRow>

                                {/* Render expanded row content */}
                                {row.getIsExpanded() && renderExpandedRow(row, index)}
                              </React.Fragment>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </SortableContext>
                </DndContext>
              </div>

              {/* Pagination footer for list view */}
              <div className="enterprise-surface-elevated flex h-12 shrink-0 items-center justify-between border-t border-border bg-card px-4">
                <div className="flex space-x-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="enterprise-button-secondary"
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                    data-cy="pagination-prev"
                  >
                    <ChevronLeft />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="enterprise-button-secondary"
                    onClick={() => table.nextPage()}
                    disabled={!canNextPage}
                    data-cy="pagination-next"
                  >
                    <ChevronRight />
                  </Button>
                </div>

                <span className="text-sm text-muted-foreground">{renderPaginationInfo()}</span>

                <div className="flex items-center space-x-2">
                  <span className="text-sm text-foreground">{i18n.getPaginationText('show')}</span>
                  <Select
                    value={String(table.getState().pagination.pageSize)}
                    onValueChange={e => {
                      const newSize = Math.min(+e, 250); // Increased limit for PIM usage
                      setPagination({ pageIndex: 0, pageSize: newSize });
                      setFilters(f => ({ ...f, page_size: newSize, page: 1 }));
                    }}
                  >
                    <SelectTrigger className="h-8 w-[70px] border-border bg-background">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[25, 50, 100, 250].map(
                        (
                          n // PIM-appropriate page sizes
                        ) => (
                          <SelectItem key={n} value={String(n)}>
                            {n}
                          </SelectItem>
                        )
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          ) : (
            <div className="flex h-64 items-center justify-center">
              <p className="text-muted-foreground">{i18n.getStateText('gridViewNotAvailable')}</p>
            </div>
          )}
        </section>
      </div>

      {/* Add modals at the end */}
      <BulkTagModal
        open={showTagModal}
        onOpenChange={setShowTagModal}
        selectedIds={getSelectedProductIds()}
        onSuccess={() => {
          setRowSelection({});
          queryClient.invalidateQueries({ queryKey: ['products'], exact: false });
        }}
      />
      <BulkCategoryModal
        open={showCategoryModal}
        onOpenChange={setShowCategoryModal}
        selectedIds={getSelectedProductIds()}
        onSuccess={() => {
          setRowSelection({});
          queryClient.invalidateQueries({ queryKey: ['products'], exact: false });
        }}
      />
      <CategoryModal
        isOpen={showCategoryManager}
        onClose={() => setShowCategoryManager(false)}
        title={i18n.getCategoryManagementText('title')}
        description={i18n.getCategoryManagementText('description')}
      />
      <ConfirmationModal
        isOpen={archiveModal.isOpen}
        onClose={archiveModal.closeModal}
        onConfirm={() => archiveModal.handleConfirm(performArchive)}
        isLoading={archiveModal.isLoading}
        variant="warning"
        title={tGlobal('productdetailpage.page.archiveModal.title')}
        description={tGlobal('productdetailpage.page.archiveModal.description')}
        confirmText={tGlobal('productdetailpage.page.archiveModal.confirmText')}
        cancelText={tGlobal('productdetailpage.page.archiveModal.cancelText')}
        warningText={tGlobal('productdetailpage.page.archiveModal.warningText')}
        metadata={
          productToArchive
            ? [
                {
                  label: tGlobal('productdetailpage.page.archiveModal.metadata.sku'),
                  value: String(productToArchive.sku ?? ''),
                },
                {
                  label: tGlobal('productdetailpage.page.archiveModal.metadata.name'),
                  value: String(productToArchive.name ?? ''),
                },
                {
                  label: tGlobal('productdetailpage.page.archiveModal.metadata.status'),
                  value: productToArchive.is_active ? 'Active' : 'Inactive',
                },
              ]
            : undefined
        }
      />
    </React.Fragment>
  );
}

// Updated CSS helper
const headerShadowStyle = `
thead tr:first-child { 
  box-shadow: 0 2px 4px rgb(0 0 0 / .05); 
}
/* still keep the left-hand divider shadow */
[data-column-id="actions"] { background: transparent !important; }
`;

// Inject the CSS style into the document head if it doesn't already exist
if (typeof document !== 'undefined') {
  const styleId = 'products-table-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = headerShadowStyle;
    document.head.appendChild(style);
  }
}
